/**
 * Home's served reads (TMMAPP-0152 §A), keyed by Home's scope (`homeScopeOf`, TMMAPP-0159 §C).
 * Each read has its own state and retry, so one failure leaves the rest of the page.
 *
 * - **The person's own books** (a SUBSCRIBER; a guest has none): `GET /v1/me/accounts`,
 *   `GET /v1/me/summary`, `GET /v1/me/review/summary` and `GET /v1/me/planned` for the next 7
 *   days (by the one window rule, `readWindow`: `from` is today, so it is left out).
 * - **Every meeting the person is in:** `GET /v1/me/meetings` over the upcoming window (the next
 *   meeting, and each Shared with you card's); a guest's space also reads its own
 *   `GET /v1/virtual-accounts/{id}/meetings`.
 * - **Goals:** `GET /v1/me/items?status=OPEN` (the person's own) or the space's open GOAL items, then
 *   the focus goal's envelope (`GET /v1/envelopes/{id}`) for its target and progress.
 * - **A guest's space:** its cash flow plan for the next 7 days, its envelopes, and its register's
 *   latest line (`GET /v1/virtual-accounts/{id}/register?limit=1`): a space's tiles read the space's
 *   data, never anyone's own books (MM-D-43; TMMAPP-0154 item 28).
 * - **Shared conversations:** `GET /v1/me/assistant/sessions?status=ACTIVE`.
 */
import { useCallback, useEffect, useState } from 'react';
import { accountsApi, type Account } from '@/api/accounts';
import { assistantApi, type AssistantSession } from '@/api/assistant';
import { envelopesApi, type Envelope } from '@/api/envelopes';
import { meetingsApi, type MeetingCalendar, type MeetingItem } from '@/api/meetings';
import { plannedApi, type Occurrence } from '@/api/planned';
import { projectionsApi, type CashflowPlan } from '@/api/projections';
import { registersApi, type VirtualAccountRegisterPage } from '@/api/registers';
import { reviewApi, type ReviewSummary } from '@/api/review';
import { summaryApi, type Summary } from '@/api/summary';
import { upcomingRange } from '@/features/meetings/meetingLists';
import { readWindow } from '@/shared/servedWindow';
import { billsWindow, focusGoalOf } from './homeRules';
import { homeScopeKey, type HomeScope } from './homeScope';

export type Read<T> =
  { status: 'loading' } | { status: 'ready'; data: T } | { status: 'error'; error: unknown };

export interface HomeRead<T> {
  read: Read<T>;
  retry: () => void;
}

const LOADING = { status: 'loading' } as const;

/** One read for `key`; `load` null skips it (it stays loading and is never shown). */
function useRead<T>(key: string, load: (() => Promise<T>) | null): HomeRead<T> {
  const [state, setState] = useState<{ key: string; read: Read<T> }>({ key, read: LOADING });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (load === null) return;
    let cancelled = false;
    load().then(
      (data) => {
        if (!cancelled) setState({ key, read: { status: 'ready', data } });
      },
      (error: unknown) => {
        if (!cancelled) setState({ key, read: { status: 'error', error } });
      },
    );
    return () => {
      cancelled = true;
    };
    // `load` is rebuilt every render; the key names what it reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt, load === null]);
  const retry = useCallback(() => {
    setState({ key, read: LOADING });
    setAttempt((n) => n + 1);
  }, [key]);
  return { read: state.key === key ? state.read : LOADING, retry };
}

export const dataOf = <T>(read: Read<T>): T | null => (read.status === 'ready' ? read.data : null);

export interface HomeData {
  accounts: HomeRead<Account[]>;
  summary: HomeRead<Summary>;
  review: HomeRead<ReviewSummary>;
  planned: HomeRead<Occurrence[]>;
  plan: HomeRead<CashflowPlan>;
  /** The scope's meetings: every meeting the person is in, or a guest's space's. */
  meetings: HomeRead<MeetingCalendar>;
  /** Every meeting the person is in (the Shared with you cards' next meetings). */
  myMeetings: HomeRead<MeetingCalendar>;
  items: HomeRead<MeetingItem[]>;
  focusEnvelope: HomeRead<Envelope>;
  envelopes: HomeRead<Envelope[]>;
  /** A space's register, its latest line only; skipped for Just me. */
  ledger: HomeRead<VirtualAccountRegisterPage>;
  sessions: HomeRead<AssistantSession[]>;
  /** The focus goal once the goals are read; null when there is none (or they failed). */
  focusGoal: MeetingItem | null;
}

export function useHomeData({
  scope,
  owner,
  today,
}: {
  scope: HomeScope;
  /** The person has books of their own (a SUBSCRIBER). */
  owner: boolean;
  today: string;
}): HomeData {
  const space = scope.kind === 'space' ? scope.virtualAccount.id : null;
  const key = `${homeScopeKey(scope)}|${today}`;
  const week = billsWindow(today);
  const upcoming = upcomingRange(today);

  const accounts = useRead(`accounts|${today}`, owner ? () => accountsApi.list() : null);
  const summary = useRead(`summary|${today}`, owner ? () => summaryApi.get() : null);
  const review = useRead(`review|${today}`, owner ? () => reviewApi.summary() : null);
  const planned = useRead(
    `planned|${today}`,
    owner ? () => readWindow(week, today, (query) => plannedApi.calendar(query), []) : null,
  );
  // The week starts today, so the one window rule leaves `from` out (TMMAPP-0159 §0).
  const plan = useRead(
    `plan|${key}`,
    space === null ? null : () => projectionsApi.virtualAccountPlan(space, { to: week.to }),
  );
  const myMeetings = useRead(`meetings|${today}`, () => meetingsApi.listMine(upcoming));
  const spaceMeetings = useRead(
    `spaceMeetings|${key}`,
    space === null ? null : () => meetingsApi.listForVirtualAccount(space, upcoming),
  );
  const meetings = space === null ? myMeetings : spaceMeetings;
  const items = useRead(`items|${key}`, () =>
    space === null
      ? meetingsApi.listMyItems('OPEN')
      : meetingsApi.listVirtualAccountItems(space, { kind: 'GOAL', status: 'OPEN' }),
  );
  const envelopes = useRead(
    `envelopes|${key}`,
    space === null ? null : () => envelopesApi.listForVirtualAccount(space),
  );
  const ledger = useRead(
    `ledger|${key}`,
    space === null ? null : () => registersApi.virtualAccountRegister(space, { limit: 1 }),
  );
  const sessions = useRead(`sessions|${today}`, () => assistantApi.list('ACTIVE'));

  const focusGoal = items.read.status === 'ready' ? focusGoalOf(items.read.data, scope) : null;
  const focusEnvelopeId = focusGoal?.envelopeId ?? null;
  const focusEnvelope = useRead(
    `focus|${focusEnvelopeId ?? 'none'}`,
    focusEnvelopeId === null ? null : () => envelopesApi.get(focusEnvelopeId),
  );

  return {
    accounts,
    summary,
    review,
    planned,
    plan,
    meetings,
    myMeetings,
    items,
    focusEnvelope,
    envelopes,
    ledger,
    sessions,
    focusGoal,
  };
}
